import React, { useState } from 'react';
import { MapPinIcon, PlusIcon, TrashIcon, TruckIcon } from 'lucide-react';
import { AppShell } from '../../../components/layout/AppShell';
import { SubNav, studioNav } from '../../../components/layout/SubNav';
import { Card, KeyValue, SectionHeading } from '../../../components/ui/Panels';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { Field, Input, Switch } from '../../../components/ui/Field';
import { ServiceAreaMap } from '../../../components/chefs/ServiceAreaMap';
import { useSession } from '../../../contexts/SessionContext';
import { useLocale } from '../../../contexts/LocaleContext';
import { currency } from '../../../utils/format';
import type { ServiceArea } from '../../../types/chef';
export function Areas() {
  const {
    chef
  } = useSession();
  const {
    locale,
    currencyCode
  } = useLocale();
  const [areas, setAreas] = useState<ServiceArea[]>(chef.serviceAreas);
  const [deliveryRadius, setDeliveryRadius] = useState(chef.deliveryFreeRadiusKm);
  const [deliveryFee, setDeliveryFee] = useState(chef.deliveryFee);
  const [newName, setNewName] = useState('');
  const update = (id: string, patch: Partial<ServiceArea>) => setAreas(areas.map(a => a.id === id ? {
    ...a,
    ...patch
  } : a));
  const add = () => {
    if (!newName.trim()) return;
    setAreas([...areas, {
      id: `area-${Date.now()}`,
      name: newName.trim(),
      districts: [],
      fulfillment: ['in_home'],
      travelIncluded: false,
      lat: chef.lat + (Math.random() - 0.5) * 0.06,
      lng: chef.lng + (Math.random() - 0.5) * 0.06,
      radiusKm: 5
    }]);
    setNewName('');
  };
  const covered = areas.reduce((sum, a) => sum + Math.round(Math.PI * a.radiusKm * a.radiusKm), 0);
  return <AppShell title="Service areas" subtitle="Requests outside these never reach your inbox" data-id="element-1185" data-mp="2s13sofjgrada">
      <SubNav items={studioNav} data-id="element-1186" data-mp="2wu6l2kci8drw" />

      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]" data-id="element-1187" data-mp="2wv81l4cahsh0">
        <div className="space-y-6" data-id="element-1188" data-mp="36om0hmsbol3x">
          <ServiceAreaMap areas={areas} center={{
          lat: chef.lat,
          lng: chef.lng
        }} className="h-[340px]" data-id="element-1189" data-mp="31qanj36qduhh" />

          <section aria-labelledby="areas-heading" data-id="element-1190" data-mp="2wqxt9xdj5l4v">
            <SectionHeading level={2} title="Where you work" hint="Solid rings include travel in your price. Dashed rings add your per-km charge to the quote automatically." data-id="element-1191" data-mp="2wpebg2c8720z" />
            <ul className="mt-4 space-y-3" data-id="element-1192" data-mp="2izszhbwufn2g">
              {areas.map(area => <Card as="li" key={area.id} className="p-4" data-id="element-1193" data-mp="2ixq1a4ljenmn">
                  <div className="flex flex-wrap items-start gap-3" data-id="element-1194" data-mp="2e1hqf3edhgla">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-soft text-primary" aria-hidden="true" data-id="element-1195" data-mp="292554n35ghkt">
                      <MapPinIcon className="h-4 w-4" data-id="element-1196" data-mp="24zi9sc79seiv" />
                    </span>
                    <div className="min-w-0 flex-1" data-id="element-1197" data-mp="201zxqcxyhj6n">
                      <div className="flex flex-wrap items-center gap-2" data-id="element-1198" data-mp="fiwgs8vhcdyy">
                        <h3 className="font-display text-base font-semibold text-ink" data-id="element-1199" data-mp="fimlc5ro1elv">{area.name}</h3>
                        {area.fulfillment.includes('delivery') && !area.fulfillment.includes('in_home') && <Badge tone="accent" data-id="element-1200" data-mp="ohrmzkyixo29">
                            <TruckIcon className="h-3 w-3" aria-hidden="true" data-id="element-1201" data-mp="thmcbk2ni5l7" /> Delivery only
                          </Badge>}
                        {area.travelIncluded ? <Badge tone="success" data-id="element-1202" data-mp="v1lqlu01xip">Travel included</Badge> : <Badge tone="neutral" data-id="element-1203" data-mp="5s1pur7agq76">{currency(chef.travelPerKm, locale, currencyCode)}/km applies</Badge>}
                      </div>
                      {!!area.districts.length && <p className="mt-1 text-[13px] text-ink-2" data-id="element-1204" data-mp="399joc8168kux">{area.districts.join(' · ')}</p>}
                    </div>
                    <button type="button" onClick={() => setAreas(areas.filter(a => a.id !== area.id))} aria-label={`Remove ${area.name}`} className="rounded p-1.5 text-ink-3 hover:text-danger" data-id="element-1205" data-mp="1kpnwz7e374pd">
                      <TrashIcon className="h-4 w-4" aria-hidden="true" data-id="element-1206" data-mp="1or98aoaz3axg" />
                    </button>
                  </div>

                  <div className="mt-4" data-id="element-1207" data-mp="23fdp1mqgtcdl">
                    <label htmlFor={`r-${area.id}`} className="flex items-baseline justify-between gap-3 text-[13px]" data-id="element-1208" data-mp="23ihz30qm32c6">
                      <span className="font-medium text-ink" data-id="element-1209" data-mp="2ch3kd3ngwvgn">Radius</span>
                      <span className="tnum text-ink-2" data-id="element-1210" data-mp="2cf0rmbuxko88">{area.radiusKm} km</span>
                    </label>
                    <input id={`r-${area.id}`} type="range" min={1} max={25} value={area.radiusKm} onChange={e => update(area.id, {
                  radiusKm: Number(e.target.value)
                })} className="mt-1.5 w-full accent-primary" data-id="element-1211" data-mp="i3cb4591wdlq" />
                  </div>

                  <div className="mt-1 border-t border-hairline" data-id="element-1212" data-mp="3qdmvmgcdvdbn">
                    <Switch checked={area.travelIncluded} onChange={on => update(area.id, {
                  travelIncluded: on
                })} label="Travel included here" description="No per-km charge appears on quotes for this ring." data-id="element-1213" data-mp="3qbah6d5u3l3c" />
                    <Switch checked={area.fulfillment.includes('in_home')} onChange={on => update(area.id, {
                  fulfillment: on ? Array.from(new Set([...area.fulfillment, 'in_home'] as const)) : area.fulfillment.filter(f => f !== 'in_home') as ServiceArea['fulfillment']
                })} label="Cook on site here" data-id="element-1214" data-mp="37eistpbfqfsf" />
                    <Switch checked={area.fulfillment.includes('delivery')} onChange={on => update(area.id, {
                  fulfillment: on ? Array.from(new Set([...area.fulfillment, 'delivery'] as const)) : area.fulfillment.filter(f => f !== 'delivery') as ServiceArea['fulfillment']
                })} label="Deliver here" data-id="element-1215" data-mp="1457cne8dr49t" />
                  </div>
                </Card>)}
            </ul>

            <Card className="mt-3 flex flex-wrap items-end gap-3 p-4" data-id="element-1216" data-mp="1d6eygwtpmcn0">
              <Field label="Add an area" id="a-new" className="min-w-[12rem] flex-1" data-id="element-1217" data-mp="2an45d4xio539">
                <Input id="a-new" value={newName} onChange={e => setNewName(e.target.value)} placeholder="Thonglor & Ekkamai" data-id="element-1218" data-mp="25v2loxy0rc37" />
              </Field>
              <Button type="button" variant="primary" onClick={add} disabled={!newName.trim()} data-id="element-1219" data-mp="2i5tydlw0tnge">
                <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-1220" data-mp="2n3ddsp4gg69x" />
                Add
              </Button>
            </Card>
          </section>
        </div>

        <aside className="space-y-4" data-id="element-1221" data-mp="2w64p8oxbz21k">
          <Card className="p-5" data-id="element-1222" data-mp="2w5341rmloz64">
            <h2 className="text-[12px] font-semibold uppercase tracking-[0.05em] text-ink-3" data-id="element-1223" data-mp="31469hd6ht7em">Coverage</h2>
            <p className="tnum mt-2 font-display text-2xl font-semibold text-ink" data-id="element-1224" data-mp="35vfz4ds1kaqi">
              {covered.toLocaleString()} km²
            </p>
            <p className="text-[13px] text-ink-2" data-id="element-1225" data-mp="3atptwn9ut9bl">across {areas.length} areas</p>
            <dl className="mt-4" data-id="element-1226" data-mp="3fudgmwr1v400">
              <KeyValue label="Travel included" value={`${chef.travelIncludedKm} km`} data-id="element-1227" data-mp="3fr967hmzrc0w" />
              <KeyValue label="Beyond that" value={`${currency(chef.travelPerKm, locale, currencyCode)} / km`} data-id="element-1228" data-mp="3jvh0duc7xk6i" />
            </dl>
          </Card>

          <Card className="p-5" data-id="element-1229" data-mp="4z6e5c3cl2cb">
            <div className="flex items-center gap-2" data-id="element-1230" data-mp="4y4rsbgvstfa">
              <TruckIcon className="h-4 w-4 text-action" aria-hidden="true" data-id="element-1231" data-mp="iynv55aejbb4" />
              <h2 className="text-[12px] font-semibold uppercase tracking-[0.05em] text-ink-3" data-id="element-1232" data-mp="iwl7ut0mvwa3">Delivery</h2>
            </div>
            <div className="mt-3 space-y-4" data-id="element-1233" data-mp="3mf4sb9d2lxuc">
              <Field label="Free within" id="a-free" hint="km" data-id="element-1234" data-mp="3mg627aicoeah">
                <Input id="a-free" type="number" min={0} value={deliveryRadius} onChange={e => setDeliveryRadius(Number(e.target.value) || 0)} className="tnum" data-id="element-1235" data-mp="3hgti2xiv5lbl" />
              </Field>
              <Field label="Flat fee beyond that" id="a-fee" data-id="element-1236" data-mp="33o4wppb2lps3">
                <Input id="a-fee" type="number" min={0} step={10} value={deliveryFee} onChange={e => setDeliveryFee(Number(e.target.value) || 0)} className="tnum" data-id="element-1237" data-mp="33mk6gbvsgt01" />
              </Field>
            </div>
            <p className="mt-3 text-[12px] leading-relaxed text-ink-3" data-id="element-1238" data-mp="1584fyuahzsrd">
              Bangkok traffic makes distance a poor proxy for time. Most chefs keep the free radius tight and charge a flat
              fee beyond it.
            </p>
          </Card>

          <Card className="p-5" data-id="element-1239" data-mp="21xdezz3yb5qb">
            <h2 className="text-[12px] font-semibold uppercase tracking-[0.05em] text-ink-3" data-id="element-1240" data-mp="21war801xdjem">Save</h2>
            <Button type="button" variant="action" full className="mt-3" data-id="element-1241" data-mp="1nwj7rjsn3at8">
              Publish area changes
            </Button>
            <p className="mt-2 text-[12px] text-ink-3" data-id="element-1242" data-mp="1suuhzvmujnbz">Live in search within a minute.</p>
          </Card>
        </aside>
      </div>
    </AppShell>;
}